import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { listGlossary } from "@/lib/academy.functions";

export const Route = createFileRoute("/academy/glossary")({
  head: () => ({
    meta: [
      { title: "The Glossary — Currency Thoughts Academy" },
      {
        name: "description",
        content:
          "Plain-English definitions for the language of financial planning — insurance, retirement, estate, and tax terms.",
      },
      { property: "og:title", content: "The Glossary — Currency Thoughts Academy" },
      {
        property: "og:description",
        content: "Plain-English definitions for the language of financial planning.",
      },
    ],
    links: [{ rel: "canonical", href: "/academy/glossary" }],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["academy", "glossary"],
      queryFn: () => listGlossary(),
    });
  },
  component: GlossaryPage,
});

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const CATEGORY_LABEL: Record<string, string> = {
  insurance: "Insurance",
  retirement: "Retirement",
  estate: "Estate",
  tax: "Tax",
  investment: "Investment",
  business: "Business",
  general: "General",
};

function GlossaryPage() {
  const { data: terms } = useSuspenseQuery({
    queryKey: ["academy", "glossary"],
    queryFn: () => listGlossary(),
  });
  const [q, setQ] = useState("");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return terms;
    return terms.filter(
      (t) =>
        t.term.toLowerCase().includes(needle) ||
        (t.short_definition ?? "").toLowerCase().includes(needle),
    );
  }, [terms, q]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    for (const t of filtered) {
      const letter = t.term[0]?.toUpperCase() ?? "#";
      const arr = map.get(letter) ?? [];
      arr.push(t);
      map.set(letter, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const featured = useMemo(() => terms.filter((t) => t.featured).slice(0, 4), [terms]);
  const availableLetters = useMemo(() => new Set(grouped.map(([l]) => l)), [grouped]);

  useEffect(() => {
    const handler = () => {
      const sections = ALPHABET.map((l) => ({
        letter: l,
        el: document.getElementById(`letter-${l}`),
      })).filter((s) => s.el);
      const top = window.scrollY + 200;
      let current: string | null = null;
      for (const s of sections) {
        if (s.el!.offsetTop <= top) current = s.letter;
      }
      setActiveLetter(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [grouped]);

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-5xl px-5 py-16 md:px-10 md:py-20 text-center">
            <div className="flex justify-center">
              <Eyebrow color="cream">Reference</Eyebrow>
            </div>
            <h1 className="mt-5 font-display text-[40px] leading-[1.1] md:text-[56px]">
              The <em className="italic text-bronze-400">Glossary</em>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl font-sans text-[17px] leading-relaxed text-cream-50/80">
              Plain-English definitions for the language of financial planning.
            </p>
            <div className="mx-auto mt-8 flex max-w-xl items-center gap-3 border border-bronze-600/40 bg-navy-800/60 px-4 py-3">
              <Search className="h-4 w-4 text-bronze-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search terms..."
                className="flex-1 bg-transparent font-sans text-[15px] text-cream-50 outline-none placeholder:text-cream-50/40"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="font-sans text-[11px] uppercase tracking-[0.18em] text-bronze-400 hover:text-bronze-600"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Alphabet nav */}
        <div className="sticky top-20 z-30 border-b border-bronze-600/20 bg-cream-50/95 backdrop-blur">
          <div className="mx-auto max-w-7xl overflow-x-auto px-5 py-3 md:px-10">
            <div className="flex items-center gap-1 md:gap-2">
              {ALPHABET.map((l) => {
                const has = availableLetters.has(l);
                const isActive = activeLetter === l;
                return (
                  <a
                    key={l}
                    href={has ? `#letter-${l}` : undefined}
                    aria-disabled={!has}
                    className={`flex h-8 w-8 items-center justify-center font-sans text-[12px] font-semibold ${
                      has
                        ? "text-navy-900 hover:text-bronze-600"
                        : "cursor-default text-charcoal-500/30"
                    } ${isActive ? "border-b-2 border-bronze-600" : ""}`}
                  >
                    {l}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Featured strip */}
        {!q && featured.length > 0 && (
          <section className="bg-cream-50 py-10">
            <div className="mx-auto max-w-7xl px-5 md:px-10">
              <Eyebrow>Most Asked</Eyebrow>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {featured.map((t) => (
                  <GlossaryCard key={t.id} term={t} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Alphabetical grid */}
        <section className="bg-cream-50 pb-24 pt-8">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            {grouped.length === 0 ? (
              <p className="py-20 text-center font-sans text-[15px] text-charcoal-500">
                No terms match "{q}".
              </p>
            ) : (
              grouped.map(([letter, items]) => (
                <div key={letter} id={`letter-${letter}`} className="scroll-mt-40 pt-12 first:pt-0">
                  <div className="flex items-baseline gap-4 border-b border-bronze-600/30 pb-3">
                    <span className="font-display text-[48px] leading-none text-bronze-600">
                      {letter}
                    </span>
                    <span className="font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-500">
                      {items.length} {items.length === 1 ? "term" : "terms"}
                    </span>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((t) => (
                      <GlossaryCard key={t.id} term={t} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function GlossaryCard({
  term,
}: {
  term: {
    slug: string;
    term: string;
    short_definition: string | null;
    category: string;
  };
}) {
  return (
    <Link
      to="/academy/glossary/$slug"
      params={{ slug: term.slug }}
      className="group flex h-full flex-col border border-bronze-600/15 bg-white p-5 transition-all hover:border-bronze-600/50 hover:shadow-[0_12px_30px_-18px_rgba(168,139,92,0.45)]"
      style={{ borderRadius: 4 }}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-[18px] leading-tight text-navy-800">{term.term}</h3>
        <span className="shrink-0 bg-bronze-400/20 px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] text-bronze-600">
          {CATEGORY_LABEL[term.category] ?? term.category}
        </span>
      </div>
      {term.short_definition && (
        <p className="mt-3 font-sans text-[13px] leading-relaxed text-charcoal-500">
          {term.short_definition}
        </p>
      )}
      <span className="mt-auto pt-4 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600 transition-transform group-hover:translate-x-1">
        Read full definition →
      </span>
    </Link>
  );
}
